import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShieldAlert, Loader2, AlertTriangle } from 'lucide-react';
import {
  useSupportAccessDisclosures,
  type SupportAccessDisclosure,
  type SupportAccessStatus,
} from '@/hooks/useSupportAccessDisclosures';

/**
 * CORE-BJ responsibility 6 — the affected-person disclosure surface.
 *
 * Passive disclosure only: this panel shows what happened when a person looks.
 * It sends no notification, and it is strictly read-only — it cannot start,
 * end, extend or modify a support session.
 */

const STATUS_PRESENTATION: Record<
  SupportAccessStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; detail: string }
> = {
  active: {
    label: 'Active now',
    variant: 'destructive',
    detail: 'This access is currently in progress.',
  },
  ended: {
    label: 'Ended',
    variant: 'secondary',
    detail: '',
  },
  expired: {
    label: 'Ended (expired)',
    variant: 'secondary',
    detail: 'The access window lapsed. The exact end time was not recorded.',
  },
  unknown: {
    label: 'End time unknown',
    variant: 'outline',
    detail: 'The related session record could not be resolved.',
  },
};

const formatMoment = (value: string) => new Date(value).toLocaleString();

const DisclosureRow = ({ disclosure }: { disclosure: SupportAccessDisclosure }) => {
  const presentation = STATUS_PRESENTATION[disclosure.status] ?? STATUS_PRESENTATION.unknown;

  return (
    <div className="p-3 rounded-md border border-border space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium">
            {disclosure.scope === 'own_account'
              ? 'Your account was accessed'
              : 'Your shop was accessed'}
          </p>
          <p className="text-xs text-muted-foreground">
            {disclosure.shop_name ?? 'Unknown shop'} · by {disclosure.operator_label}
          </p>
        </div>
        <Badge variant={presentation.variant}>{presentation.label}</Badge>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div>
          <dt className="inline text-muted-foreground">Began: </dt>
          <dd className="inline">{formatMoment(disclosure.started_at)}</dd>
        </div>
        <div>
          <dt className="inline text-muted-foreground">Ended: </dt>
          <dd className="inline">
            {disclosure.ended_at ? formatMoment(disclosure.ended_at) : 'Not recorded'}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="inline text-muted-foreground">Reason: </dt>
          <dd className="inline">{disclosure.reason || 'No reason was recorded.'}</dd>
        </div>
      </dl>

      {presentation.detail && (
        <p className="text-xs text-muted-foreground">{presentation.detail}</p>
      )}
    </div>
  );
};

export const SupportAccessDisclosurePanel = () => {
  const { data, isLoading, isError } = useSupportAccessDisclosures();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5" />
          Support Access
        </CardTitle>
        <CardDescription>
          When OptiBay support enters your shop or your account, it is recorded here.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div
            className="flex items-center justify-center py-4"
            data-testid="support-access-loading"
          >
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          // Never render the empty state on failure. "We could not load this"
          // and "this did not happen" are different statements, and only one of
          // them is honest here.
          <Alert variant="destructive" data-testid="support-access-error">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              We could not load your support access record. This does not mean no access
              occurred — please try again.
            </AlertDescription>
          </Alert>
        ) : !data?.length ? (
          <p className="text-sm text-muted-foreground" data-testid="support-access-empty">
            No support access to your account or shop has been recorded.
          </p>
        ) : (
          <div className="space-y-2" data-testid="support-access-list">
            {data.map(disclosure => (
              <DisclosureRow key={disclosure.notice_id} disclosure={disclosure} />
            ))}
          </div>
        )}

        {/* Honest coverage statement. Since BJ-N5/BJ-N6 this covers both
            shop-scoped access and account impersonation, each carrying the
            reason recorded when the access was authorised. */}
        <p className="text-xs text-muted-foreground mt-4">
          This record covers support access to your shop and to your own account.
        </p>
      </CardContent>
    </Card>
  );
};
